import { useCallback, useEffect, useState } from 'react'
import s from './styles.module.scss'
import * as api from 'api'
import { Alert, Layouts, Loader, NoData } from 'components'
import { head, getTimeFormats } from 'helpers'
import { Link } from 'react-router-dom'

const REVENUE_PERIODS = [
  { key: 'daily', label: 'Today' },
  { key: 'weekly', label: 'Week' },
  { key: 'monthly', label: 'Month' },
  { key: 'yearly', label: 'Year' },
]

const EMPTY_PAYMENT_PERIOD = { count: 0, revenue: 0, records: [] }

const PERIOD_LABELS = {
  daily: 'today',
  weekly: 'this week',
  monthly: 'this month',
  yearly: 'this year',
}

export default function CRMDashboard() {
  const [loading, setLoading] = useState(false)
  const [revenuePeriod, setRevenuePeriod] = useState('monthly')
  const [stats, setStats] = useState({
    users: { total: 0, newToday: 0, paidActive: 0, pendingPayment: 0 },
    businesses: { total: 0, pending: 0, active: 0 },
    listings: { total: 0, pending: 0, active: 0 },
    calls: { today: 0, week: 0, followUps: 0 },
    leads: { today: 0, week: 0, conversion: 0 },
    payments: {
      daily: { ...EMPTY_PAYMENT_PERIOD },
      weekly: { ...EMPTY_PAYMENT_PERIOD },
      monthly: { ...EMPTY_PAYMENT_PERIOD },
      yearly: { ...EMPTY_PAYMENT_PERIOD },
    },
  })
  const [recentCalls, setRecentCalls] = useState([])
  const [recentUsers, setRecentUsers] = useState([])

  const fetchDashboardData = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch CRM statistics (businesses, listings, leads, payments)
      const crmStats = await api.admin.crm.getStatistics({})
      if (crmStats.code === 200) {
        setStats(prev => ({
          ...prev,
          businesses: {
            total: crmStats.payload.businesses?.total || 0,
            pending: crmStats.payload.businesses?.pending || 0,
            active: crmStats.payload.businesses?.active || 0
          },
          listings: {
            total: crmStats.payload.listings?.total || 0,
            pending: crmStats.payload.listings?.pending || 0,
            active: crmStats.payload.listings?.active || 0
          },
          leads: {
            today: crmStats.payload.leads?.today || 0,
            week: crmStats.payload.leads?.week || 0,
            conversion: crmStats.payload.leads?.conversion || 0
          },
          payments: {
            daily: crmStats.payload.payments?.daily || { ...EMPTY_PAYMENT_PERIOD },
            weekly: crmStats.payload.payments?.weekly || { ...EMPTY_PAYMENT_PERIOD },
            monthly: crmStats.payload.payments?.monthly || { ...EMPTY_PAYMENT_PERIOD },
            yearly: crmStats.payload.payments?.yearly || { ...EMPTY_PAYMENT_PERIOD },
          },
          users: {
            total: crmStats.payload.users?.total || 0,
            paidActive: crmStats.payload.users?.paidActive || 0,
            pendingPayment: crmStats.payload.users?.pendingPayment || 0,
            newToday: crmStats.payload.users?.newToday || 0,
          },
        }))
      }

      // Fetch call statistics
      const callStats = await api.admin.call.getStatistics({})
      if (callStats.code === 200) {
        setStats(prev => ({
          ...prev,
          calls: {
            today: callStats.payload.today.total,
            week: callStats.payload.week.total,
            followUps: callStats.payload.followUpsRequired
          }
        }))
      }

      // Fetch recent calls
      const calls = await api.admin.call.fetchAll({ perPage: 10, pageNo: 1 })
      if (calls.code === 200) {
        setRecentCalls(calls.payload.calls || [])
      }

      // Fetch users
      const usersResponse = await api.admin.user.fetchAll({
        limit: 10,
        pageNo: 1
      })
      if (usersResponse.code === 200) {
        setRecentUsers(usersResponse.payload.Users || [])
      }
    } catch (error) {
      console.error('Failed to load CRM dashboard:', error)
      Alert.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    head({ title: 'CRM Dashboard | BCN Admin' })
    fetchDashboardData()
  }, [fetchDashboardData])

  return (
    <div className={s.page}>
      <Layouts.Classic title="CRM Dashboard">
        <div className={s.headerActions}>
          <Link to="/admin/crm/calls" className={s.secondaryBtn}>
            <span className="material-icons-outlined">phone</span>
            Manage Calls
          </Link>
          <Link to="/admin/crm/contacts" className={s.secondaryBtn}>
            <span className="material-icons-outlined">contacts</span>
            Contacts
          </Link>
          <Link to="/admin/analytics/activity" className={s.secondaryBtn}>
            <span className="material-icons-outlined">insights</span>
            Activity Analytics
          </Link>
        </div>
      </Layouts.Classic>

      <div className={s.hero}>
        <p className={s.heroText}>
          Overview of members, businesses, calls, leads, and revenue. Jump into calls or contacts to follow up with members.
        </p>
      </div>

      {loading ? (
        <div className={s.loaderWrap}>
          <Loader color="var(--c-primary)" colorText="var(--c-primary)" />
        </div>
      ) : (
        <div className={s.content}>
          {/* Key Metrics */}
          <div className={s.metricsGrid}>
            <MetricCard
              title='Total Users'
              value={stats.users.total}
              subtitle={`${stats.users.paidActive} paid · ${stats.users.pendingPayment} pending · ${stats.users.newToday} new today`}
              icon='people'
              color='blue'
              link='/admin/users'
            />
            <MetricCard
              title='Businesses'
              value={stats.businesses.total}
              subtitle={`${stats.businesses.pending} pending approval`}
              icon='business'
              color='green'
            />
            <MetricCard
              title='Calls Today'
              value={stats.calls.today}
              subtitle={`${stats.calls.followUps} follow-ups required`}
              icon='phone'
              color='orange'
              link='/admin/crm/calls'
            />
            <MetricCard
              title='Leads Today'
              value={stats.leads.today}
              subtitle={`${stats.leads.conversion}% conversion`}
              icon='trending_up'
              color='purple'
            />
            <MetricCard
              title='Listings'
              value={stats.listings.total}
              subtitle={`${stats.listings.pending} pending`}
              icon='list'
              color='teal'
            />
            <RevenueMetricCard
              payments={stats.payments}
              period={revenuePeriod}
              onPeriodChange={setRevenuePeriod}
            />
          </div>

          {/* Membership vs payments clarity */}
          <div className={s.membershipBar}>
            <div className={s.membershipStat}>
              <span className={s.membershipLabel}>All registered</span>
              <strong>{stats.users.total}</strong>
            </div>
            <div className={s.membershipDivider} />
            <div className={s.membershipStat}>
              <span className={s.membershipLabel}>Paid (active plan)</span>
              <strong className={s.paid}>{stats.users.paidActive}</strong>
            </div>
            <div className={s.membershipDivider} />
            <div className={s.membershipStat}>
              <span className={s.membershipLabel}>Not paid / expired</span>
              <strong className={s.pending}>{stats.users.pendingPayment}</strong>
            </div>
            <p className={s.membershipNote}>
              Revenue below counts only <strong>membership payments received</strong> in the selected period.
              Donations are excluded. &quot;Unknown&quot; rows were unlinked payments (now auto-resolved where possible).
            </p>
          </div>

          {/* Payment details for selected period */}
          <PaymentDetailsPanel
            payments={stats.payments}
            period={revenuePeriod}
          />

          {/* Recent Users */}
          <div className={s.panel}>
            <div className={s.sectionHeader}>
              <div>
                <h2>Recent Users</h2>
                <p className={s.sectionMeta}>Latest member registrations</p>
              </div>
              <Link to="/admin/users" className={s.viewAll}>View all</Link>
            </div>
            {recentUsers.length === 0 ? (
              <NoData message='No users found' />
            ) : (
              <div className={s.usersList}>
                {recentUsers.map(user => (
                  <UserCard key={user._id} user={user} />
                ))}
              </div>
            )}
          </div>

          {/* Recent Calls */}
          <div className={s.panel}>
            <div className={s.sectionHeader}>
              <div>
                <h2>Recent Calls</h2>
                <p className={s.sectionMeta}>{stats.calls.followUps} follow-ups required</p>
              </div>
              <Link to="/admin/crm/calls" className={s.viewAll}>View all</Link>
            </div>
            {recentCalls.length === 0 ? (
              <NoData message='No calls recorded yet' />
            ) : (
              <div className={s.callsList}>
                {recentCalls.map(call => (
                  <CallCard key={call._id} call={call} />
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className={s.panel}>
            <div className={s.sectionHeader}>
              <div>
                <h2>Quick Actions</h2>
                <p className={s.sectionMeta}>Common admin workflows</p>
              </div>
            </div>
            <div className={s.quickActions}>
              <Link to="/admin/users" className={s.quickAction}>
                <span className="material-icons-outlined">person_add</span>
                <span>Add User</span>
              </Link>
              <Link to="/admin/approvalRequests" className={s.quickAction}>
                <span className="material-icons-outlined">pending_actions</span>
                <span>Review Approvals</span>
              </Link>
              <Link to="/admin/aadhaarVerification" className={s.quickAction}>
                <span className="material-icons-outlined">verified_user</span>
                <span>Aadhaar Verification</span>
              </Link>
              <Link to="/admin/crm/calls" className={s.quickAction}>
                <span className="material-icons-outlined">phone_in_talk</span>
                <span>Make Call</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const PaymentDetailsPanel = ({ payments, period }) => {
  const current = payments[period] || EMPTY_PAYMENT_PERIOD
  const records = current.records || []
  const periodLabel = PERIOD_LABELS[period] || period

  return (
    <div className={s.panel}>
      <div className={s.sectionHeader}>
        <div>
          <h2>Payment details ({periodLabel})</h2>
          <p className={s.sectionMeta}>
            {current.count} payment{current.count === 1 ? '' : 's'} · ₹{current.revenue.toLocaleString('en-IN')} total
          </p>
        </div>
        <Link to="/admin/users" className={s.viewAll}>Manage users</Link>
      </div>

      {records.length === 0 ? (
        <NoData message={`No payments received ${periodLabel}`} />
      ) : (
        <div className={s.paymentsTableWrap}>
          <table className={s.paymentsTable}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Phone</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {records.map(record => (
                <tr key={record.id}>
                  <td>
                    <div className={s.paymentUserName}>{record.userName}</div>
                    {record.userRefId && (
                      <div className={s.paymentUserRef}>ID: {record.userRefId}</div>
                    )}
                    {record.note && (
                      <div className={s.paymentNote}>{record.note}</div>
                    )}
                  </td>
                  <td>{record.phoneNo || '—'}</td>
                  <td><span className={s.planTag}>{record.plan}</span></td>
                  <td className={s.amountCell}>₹{Number(record.amount).toLocaleString('en-IN')}</td>
                  <td><span className={s.methodTag}>{record.method}</span></td>
                  <td>{record.paidAt ? getTimeFormats(new Date(record.paidAt)) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const RevenueMetricCard = ({ payments, period, onPeriodChange }) => {
  const periodLabels = {
    daily: 'Today',
    weekly: 'This week',
    monthly: 'This month',
    yearly: 'This year',
  }
  const current = payments[period] || { count: 0, revenue: 0 }
  const paymentLabel = current.count === 1 ? 'payment' : 'payments'

  return (
    <div className={`${s.metricCard} ${s['metricCard-red']} ${s.revenueCard}`}>
      <div className={s.metricIcon}>
        <span className='material-icons-outlined'>payments</span>
      </div>
      <div className={s.metricContent}>
        <div className={s.revenueHeader}>
          <div className={s.metricTitle}>Revenue ({periodLabels[period]})</div>
          <div className={s.periodToggle} role="tablist" aria-label="Revenue period">
            {REVENUE_PERIODS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={period === key}
                className={period === key ? s.periodActive : s.periodBtn}
                onClick={() => onPeriodChange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className={s.metricValue}>₹{current.revenue.toLocaleString('en-IN')}</div>
        <div className={s.metricSubtitle}>
          {current.count} {paymentLabel}
        </div>
      </div>
    </div>
  )
}

const MetricCard = ({ title, value, subtitle, icon, color, link }) => {
  const content = (
    <div className={`${s.metricCard} ${s[`metricCard-${color}`]}`}>
      <div className={s.metricIcon}>
        <span className='material-icons-outlined'>{icon}</span>
      </div>
      <div className={s.metricContent}>
        <div className={s.metricTitle}>{title}</div>
        <div className={s.metricValue}>{value}</div>
        {subtitle && <div className={s.metricSubtitle}>{subtitle}</div>}
      </div>
    </div>
  )

  return link ? (
    <Link to={link} className={s.metricLink}>
      {content}
    </Link>
  ) : (
    content
  )
}

const UserCard = ({ user }) => {
  return (
    <Link to={`/admin/users`} className={s.userCard}>
      <div className={s.userHeader}>
        <div className={s.userAvatar}>
          {(user.firstName?.[0] || 'U').toUpperCase()}
        </div>
        <div className={s.userInfo}>
          <div className={s.userName}>
            {user.firstName} {user.lastName}
          </div>
          <div className={s.userPhone}>{user.phoneNo}</div>
          {user.userRefId && (
            <div className={s.userRefId}>ID: {user.userRefId}</div>
          )}
        </div>
        <div className={s.userStatus}>
          {user.isApproved ? (
            <span className={s.statusApproved}>Approved</span>
          ) : (
            <span className={s.statusPending}>Pending</span>
          )}
        </div>
      </div>
      <div className={s.userDetails}>
        <div className={s.userDetail}>
          <span className='material-icons-outlined'>location_on</span>
          <span>{user.city || 'N/A'}, {user.state || 'N/A'}</span>
        </div>
        {user.email && (
          <div className={s.userDetail}>
            <span className='material-icons-outlined'>email</span>
            <span>{user.email}</span>
          </div>
        )}
        <div className={s.userDetail}>
          <span className='material-icons-outlined'>event</span>
          <span>{user.createdAt ? getTimeFormats(new Date(user.createdAt)) : '—'}</span>
        </div>
      </div>
    </Link>
  )
}

const CallCard = ({ call }) => {
  const getStatusColor = (status) => {
    const colors = {
      completed: 'green',
      answered: 'blue',
      missed: 'red',
      busy: 'orange',
      failed: 'red',
      pending: 'gray'
    }
    return colors[status] || 'gray'
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '—'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className={s.callCard}>
      <div className={s.callHeader}>
        <div className={s.callInfo}>
          <div className={s.callName}>
            {call.contactName || call.userId?.firstName || 'Unknown'}
          </div>
          <div className={s.callPhone}>{call.phoneNumber}</div>
        </div>
        <div className={`${s.callStatus} ${s[`status-${getStatusColor(call.status)}`]}`}>
          {call.status}
        </div>
      </div>
      <div className={s.callDetails}>
        <div className={s.callDetail}>
          <span className='material-icons-outlined'>call</span>
          <span>{call.direction === 'inbound' ? 'Inbound' : 'Outbound'}</span>
        </div>
        {call.duration > 0 && (
          <div className={s.callDetail}>
            <span className='material-icons-outlined'>schedule</span>
            <span>{formatDuration(call.duration)}</span>
          </div>
        )}
        <div className={s.callDetail}>
          <span className='material-icons-outlined'>event</span>
          <span>{call.startTime ? getTimeFormats(new Date(call.startTime)) : '—'}</span>
        </div>
      </div>
      {call.notes && (
        <div className={s.callNotes}>{call.notes}</div>
      )}
    </div>
  )
}

